/**
 * Client-side document corner detection.
 *
 * Ports the preprocessing/postprocessing of
 * agents/document_verification/app/services/local_aligner.py for the
 * lcnet100_h_e_bifpn_256 model — same architecture family as the production
 * fastvit_sa24 model (4-channel heatmap -> contour-centroid decode), just a
 * smaller backbone. Chosen over the simpler lcnet050 direct-regression
 * candidate after real-device testing showed lcnet050's corners
 * under-bounding the document (Phase 1 eval already measured this: lcnet100
 * was 0.7-5.8px mean corner error vs. lcnet050's 8.6-18.3px on the same test
 * images).
 *
 * Shipped as float16 (converted with keep_io_types=True, so this module's
 * I/O tensors stay float32 — no code-level difference from the original
 * fp32 export). Verified against the fp32 source on the real
 * onnxruntime-web wasm backend before the swap: Pearson correlation 0.9999,
 * max per-element diff 0.004 on the raw heatmap output. Halves the download
 * (4.8MB -> 2.4MB). Dynamic int8 quantization was tried and rejected — it
 * loads on this wasm backend but produces heatmap output uncorrelated with
 * the fp32 model's (measured correlation ~0.05); ORT's dynamic quantizer
 * targets MatMul-heavy models, not this Conv+BiFPN architecture.
 *
 * Also stripped of identifying metadata/node names and served from
 * core (GET /api/v1/assets/document-aligner-model/<hash>, session-authenticated
 * — see AssetController.java) instead of a plain public static file, so it
 * isn't a trivially scrapable, architecture-labeled asset for anyone who
 * isn't mid-verification. This raises the bar against casual/bulk
 * scraping; it does not and cannot stop a real user's browser, which has
 * to receive the plaintext bytes to run the model at all, from extracting
 * it deliberately.
 *
 * Bundled in .ort format (not .onnx) because public/ort/'s WASM binary is a
 * custom minimal onnxruntime-web build — .onnx support is compiled out
 * entirely on a minimal build (confirmed: loading .onnx against it fails
 * with "ONNX format model is not supported in this build"). That build cut
 * ort-wasm-simd-threaded.wasm from 13.6MB to 3.5MB by compiling in only the
 * ~15 op types this model actually uses (generated from this exact model via
 * onnxruntime's create_reduced_build_config.py, not guessed) plus type
 * reduction (float/float16 kernels only). Verified byte-identical heatmap
 * output against the original full build before the swap.
 *
 * To rebuild (matches onnxruntime-web's package.json version):
 *   git clone --branch v1.30.0 --recurse-submodules --shallow-submodules \
 *     https://github.com/microsoft/onnxruntime
 *   python3 -m onnxruntime.tools.convert_onnx_models_to_ort \
 *     --optimization_style Fixed --enable_type_reduction <model>.onnx
 *   (produces <model>.ort + required_operators_and_types.config)
 *   python3 onnxruntime/tools/ci_build/build.py --config Release \
 *     --build_wasm --enable_wasm_simd --enable_wasm_threads \
 *     --minimal_build extended \
 *     --include_ops_by_config required_operators_and_types.config \
 *     --enable_reduced_operator_type_support \
 *     --disable_ml_ops --disable_generation_ops --skip_tests
 *   (needs emsdk 4.0.20 activated, and Python's `flatbuffers` package
 *   installed — build.py's own reduce_op_kernels.py silently can't import
 *   parse_config without it, which surfaces as a confusing ImportError, not
 *   a "pip install this" message.) Output: build/Release/ort-wasm-simd-
 *   threaded.{wasm,mjs} -> copy into public/ort/. The model must ship as
 *   the paired .ort file, not .onnx — see AssetController.java.
 */
import axios from "axios";
import { loadOpenCv, MatTracker } from "./openCvLoader";

// The hash segment must match AssetController's MODEL_VERSION — the browser
// caches this response for 24h, so a new model needs a new URL (this path)
// to avoid a stale cached model running against this file's fixed input
// size, border padding and heatmap threshold, which are tuned to one model.
const MODEL_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/api/v1/assets/document-aligner-model/bd09597de6c3`;
const PAD = 100;
const INFER_SIZE = 256;
const HEATMAP_THRESHOLD = 0.3;

let sessionPromise = null;

/**
 * Loads the ONNX Runtime Web session (WASM backend) once per tab and caches
 * it at module scope — reused across component mounts (e.g. front → back
 * capture in one verification session) without re-downloading. Fetches the
 * model bytes via an authenticated request (same X-CLIENT-ID + bearer
 * pattern as every other direct-link call) rather than handing ort a bare
 * URL, since the model is no longer a public static asset.
 */
export function loadAligner(clientId, accessToken) {
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const ort = await import("onnxruntime-web/wasm");
      ort.env.wasm.wasmPaths = "/ort/";
      ort.env.wasm.numThreads = 1; // no COOP/COEP on this app, so no cross-origin isolation for threads

      const { data } = await axios.get(MODEL_ENDPOINT, {
        responseType: "arraybuffer",
        headers: { "X-CLIENT-ID": clientId, "Authorization": `Bearer ${accessToken}` },
      });

      const session = await ort.InferenceSession.create(new Uint8Array(data), {
        executionProviders: ["wasm"],
      });
      return { ort, session };
    })().catch((err) => {
      sessionPromise = null;
      throw err;
    });
  }
  return sessionPromise;
}

/**
 * Pads `canvas` 100px on every side (black, matches cv2.copyMakeBorder in
 * local_aligner.py) then squish-resizes to 256x256 for the model input.
 */
function padAndResize(canvas) {
  const srcW = canvas.width;
  const srcH = canvas.height;
  const paddedW = srcW + PAD * 2;
  const paddedH = srcH + PAD * 2;

  const padCanvas = document.createElement("canvas");
  padCanvas.width = paddedW;
  padCanvas.height = paddedH;
  const padCtx = padCanvas.getContext("2d");
  padCtx.fillStyle = "#000000";
  padCtx.fillRect(0, 0, paddedW, paddedH);
  padCtx.drawImage(canvas, PAD, PAD);

  const resizedCanvas = document.createElement("canvas");
  resizedCanvas.width = INFER_SIZE;
  resizedCanvas.height = INFER_SIZE;
  resizedCanvas.getContext("2d").drawImage(
    padCanvas, 0, 0, paddedW, paddedH, 0, 0, INFER_SIZE, INFER_SIZE
  );

  return { resizedCanvas, paddedW, paddedH };
}

/**
 * Builds the NCHW float32 [1,3,256,256] tensor, /255 normalized, in BGR
 * channel order — the production model reads images via cv2 (BGR) and never
 * converts to RGB before feeding the network, so channel order must match.
 * Canvas ImageData is RGBA; we reorder R/G/B -> B/G/R here.
 */
function toBgrChwTensor(ort, resizedCanvas) {
  const ctx = resizedCanvas.getContext("2d");
  const { data } = ctx.getImageData(0, 0, INFER_SIZE, INFER_SIZE);
  const plane = INFER_SIZE * INFER_SIZE;
  const chw = new Float32Array(3 * plane);

  for (let i = 0; i < plane; i++) {
    const o = i * 4;
    chw[i] = data[o + 2] / 255; // B
    chw[plane + i] = data[o + 1] / 255; // G
    chw[2 * plane + i] = data[o] / 255; // R
  }

  return new ort.Tensor("float32", chw, [1, 3, INFER_SIZE, INFER_SIZE]);
}

/**
 * Decodes one heatmap channel (native heatmapH x heatmapW, values ~0-1,
 * already sigmoid-activated by the model) into a single (x, y) point in
 * `dstW x dstH` (the padded-image) space. Mirrors local_aligner.py's
 * _decode_heatmaps / _centroid_of_largest_polygon: upscale -> zero anything
 * below threshold -> scale to 0-255 -> Otsu-binarize -> take the largest
 * contour's centroid.
 */
function decodeHeatmapChannel(cv, tracker, channelData, heatmapH, heatmapW, dstW, dstH) {
  const src = tracker.add(new cv.Mat(heatmapH, heatmapW, cv.CV_32FC1));
  src.data32F.set(channelData);

  const resized = tracker.add(new cv.Mat());
  cv.resize(src, resized, new cv.Size(dstW, dstH), 0, 0, cv.INTER_LINEAR);

  // pred[pred < threshold] = 0
  cv.threshold(resized, resized, HEATMAP_THRESHOLD, 0, cv.THRESH_TOZERO);

  // pred = np.uint8(pred * 255)
  const scaled = tracker.add(new cv.Mat());
  resized.convertTo(scaled, cv.CV_8UC1, 255.0);

  // Otsu binarize (cb.imbinarize)
  const binary = tracker.add(new cv.Mat());
  cv.threshold(scaled, binary, 0, 255, cv.THRESH_BINARY + cv.THRESH_OTSU);

  const contours = tracker.add(new cv.MatVector());
  const hierarchy = tracker.add(new cv.Mat());
  cv.findContours(binary, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

  let bestArea = 0;
  let bestPoint = null;
  for (let i = 0; i < contours.size(); i++) {
    const contour = contours.get(i);
    const area = cv.contourArea(contour);
    if (area > bestArea) {
      const m = cv.moments(contour);
      if (m.m00 > 1e-5) {
        bestArea = area;
        bestPoint = { x: m.m10 / m.m00, y: m.m01 / m.m00 };
      }
    }
    contour.delete();
  }

  return bestPoint;
}

/**
 * Runs the aligner on the given canvas (a snapshot of the current camera
 * frame at native resolution). Returns 4 corners in original-image pixel
 * space plus a hasObject flag (true only if all 4 heatmap channels produced
 * a contour, mirroring local_aligner.py's `len(corners) < 4 -> None`).
 */
export async function runAligner(canvas) {
  const [{ ort, session }, cv] = await Promise.all([loadAligner(), loadOpenCv()]);
  const { resizedCanvas, paddedW, paddedH } = padAndResize(canvas);
  const tensor = toBgrChwTensor(ort, resizedCanvas);

  const results = await session.run({ img: tensor });
  const heatmap = results.heatmap;
  const [, numChannels, heatmapH, heatmapW] = heatmap.dims;
  const plane = heatmapH * heatmapW;

  const tracker = new MatTracker();
  try {
    const corners = [];
    for (let c = 0; c < numChannels; c++) {
      const channelData = heatmap.data.subarray(c * plane, (c + 1) * plane);
      const point = decodeHeatmapChannel(cv, tracker, channelData, heatmapH, heatmapW, paddedW, paddedH);
      if (point) {
        corners.push({ x: point.x - PAD, y: point.y - PAD });
      }
    }

    return { corners, hasObject: corners.length === 4 };
  } finally {
    tracker.deleteAll();
  }
}
